import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogFocusOptions {
  readonly open: Ref<boolean>;
  readonly panelId: string;
  readonly onClose: () => void;
  readonly canClose?: () => boolean;
}

export interface DialogFocus {
  onKeydown(event: KeyboardEvent): void;
}

function focusable(panelId: string): HTMLElement[] {
  const panel = document.getElementById(panelId);
  return panel === null ? [] : Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
}

function trapTab(event: KeyboardEvent, panelId: string): void {
  const nodes = focusable(panelId);
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (first === undefined || last === undefined) {
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

export function useDialogFocus(options: DialogFocusOptions): DialogFocus {
  let lastFocused: HTMLElement | null = null;

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (options.canClose === undefined || options.canClose()) {
        options.onClose();
      }
      return;
    }
    if (event.key === 'Tab') {
      trapTab(event, options.panelId);
    }
  }

  watch(options.open, async (open) => await onOpenChanged(open), {
    immediate: true,
    flush: 'post',
  });

  async function onOpenChanged(open: boolean): Promise<void> {
    if (open) {
      lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      document.body.style.overflow = 'hidden';
      await nextTick();
      focusable(options.panelId)[0]?.focus();
      return;
    }
    document.body.style.overflow = '';
    lastFocused?.focus();
    lastFocused = null;
  }

  onBeforeUnmount(() => {
    document.body.style.overflow = '';
  });

  return { onKeydown };
}
